import { Link, useLocation } from "wouter";
import { SEOHead, ORGANIZATION_SCHEMA } from "@/components/seo-head";
import { BLOG_POSTS } from "@/lib/blog-data";
import { Button } from "@/components/ui/button";
import { SeoPageHeader } from "@/components/seo-page-header";
import { ArrowRight, BookOpen, ChevronRight, Clock } from "lucide-react";
import { SeoPageFooter } from "@/components/seo-page-footer";

const PILLAR_GUIDES = [
  {
    href: "/guides/quoting-for-uk-builders/",
    title: "Quoting for UK Builders: The Complete Guide (2026)",
    summary:
      "Everything UK builders need to know about quoting jobs accurately: what quoting software does, how to price construction jobs, UK cost benchmarks, AI vs manual quoting, and the most common mistakes that cost builders money.",
    readTime: "18 min read",
  },
  {
    href: "/guides/running-a-building-business-uk/",
    title: "Running a Building Business in the UK: The Complete Guide (2026)",
    summary:
      "A practical guide to running a building business in the UK: business structure, insurance requirements, HMRC Making Tax Digital, managing subcontractors, quoting accurately, and keeping cash flow healthy.",
    readTime: "20 min read",
  },
];

const BLOG_ITEM_LIST_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": "The Proper Tool Blog & Guides",
  "description": "Guides, tips and resources for UK builders on quoting, pricing and winning more building work.",
  "url": "https://thepropertool.com/blog",
  "itemListElement": BLOG_POSTS.map((post, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "url": `https://thepropertool.com/blog/${post.slug}`,
    "name": post.title,
  })),
};

const BLOG_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Blog",
  "name": "The Proper Tool: Blog & Guides",
  "description": "Guides, tips and resources for UK builders on quoting, pricing and winning more building work.",
  "url": "https://thepropertool.com/blog",
  "publisher": {
    "@type": "Organization",
    "name": "The Proper Tool",
    "url": "https://thepropertool.com"
  }
};

const BLOG_BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": "https://thepropertool.com"
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": "Blog & Guides",
      "item": "https://thepropertool.com/blog"
    }
  ]
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

export default function BlogIndexPage() {
  const [, navigate] = useLocation();

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="Blog & Guides: Tips and Articles for UK Builders"
        description="Practical guides for UK builders on how to price building jobs, use quoting software, and win more construction work."
        canonical="/blog"
        ogType="website"
        schema={[BLOG_ITEM_LIST_SCHEMA, BLOG_SCHEMA, BLOG_BREADCRUMB_SCHEMA, ORGANIZATION_SCHEMA]}
      />

      <SeoPageHeader />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="mb-12">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
            <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <span>Blog &amp; Guides</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Blog &amp; Guides</h1>
          <p className="text-lg text-muted-foreground max-w-2xl">
            Practical guides on pricing building jobs, choosing quoting software, and running a more profitable building business.
          </p>
        </div>

        <section className="mb-16">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">In-depth Guides</h2>
            <Link href="/guides/" className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">
              All guides <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 mb-16">
            {PILLAR_GUIDES.map((guide) => (
              <Link key={guide.href} href={guide.href}>
                <div className="group border border-border rounded-xl p-6 hover:border-primary/40 hover:bg-primary/5 transition-all cursor-pointer h-full flex flex-col">
                  <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <BookOpen className="h-5 w-5 text-primary" />
                  </div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">{guide.readTime}</p>
                  <h3 className="font-semibold text-base leading-snug mb-2 group-hover:text-primary transition-colors">{guide.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed flex-1">{guide.summary}</p>
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary mt-4 group-hover:gap-2 transition-all">
                    Read guide <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold mb-6">Articles</h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {BLOG_POSTS.map((post) => (
              <article
                key={post.slug}
                className="group border border-border rounded-xl p-6 hover:border-primary/50 hover:shadow-md transition-all cursor-pointer h-full flex flex-col"
                onClick={() => navigate(`/blog/${post.slug}`)}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary">{post.category}</span>
                  <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" />{post.readTime}</span>
                </div>
                <h3 className="font-semibold text-lg leading-snug mb-3 group-hover:text-primary transition-colors">{post.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed flex-1">{post.excerpt}</p>
                {post.relatedPosts && post.relatedPosts.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-border/60">
                    <p className="text-xs font-medium text-muted-foreground mb-1.5">Related reading</p>
                    <ul className="space-y-1">
                      {post.relatedPosts.slice(0, 2).map((related) => (
                        <li key={related.slug}>
                          <Link
                            href={`/blog/${related.slug}`}
                            className="text-xs text-primary/80 hover:text-primary hover:underline line-clamp-1 transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {related.title}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="flex items-center justify-between mt-4">
                  <div className="flex flex-col gap-0.5">
                    {post.author && (
                      <span className="text-xs text-muted-foreground">
                        By{" "}
                        {post.author.name}
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground">{formatDate(post.publishedDate)}</span>
                  </div>
                  <div className="flex items-center gap-1 text-sm font-medium text-primary ml-auto">
                    Read article <ChevronRight className="h-4 w-4" />
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="bg-primary/5 border border-primary/20 rounded-2xl p-8 text-center">
          <BookOpen className="h-8 w-8 text-primary mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-3">Ready to quote faster?</h2>
          <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
            The Proper Tool is builder quoting software built for UK tradespeople. Generate a professional quote from drawings or a job description in under 10 minutes.
          </p>
          <Button size="lg" asChild>
            <Link href="/login">Start free, no card needed</Link>
          </Button>
        </section>
      </main>

      <SeoPageFooter />
    </div>
  );
}
